import React, { useState } from "react";
import { DotLegalFullScreenSpinner, DotLegalPageHeader } from "@dotlegal/dotlegal-ui-components";
import DotLegalStepper from "../common/components/dotLegalStepper/DotLegalStepper.tsx";
import { EntityTemplateField } from "./EntityTemplate.types.ts";
import { useEditEntityTemplate } from "./EditEntityTemplate.hooks.ts";
import { useUrlProvider } from "../useUrlProvider.ts";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "../localization/useTranslation.ts";
import TemplateStepEditor from "./TemplateStepEditor.tsx";

export interface IEditEntityTemplatesProps {}

function EditEntityTemplate(props: IEditEntityTemplatesProps) {
    const urlProvider = useUrlProvider();
    const navigate = useNavigate();

    const { id } = useParams<{ id: string }>();
    const { translateString } = useTranslation();
    const {
        isLoading,
        breadCrumbs,
        templateFromBackend,
        availableStandardFields,
        customInputFields,
        handleFieldReorder,
        handleFieldDelete,
        handleFieldRequiredToggle,
        handleFieldRequiredAtCreationToggle,
        handleFieldAdd,
        handleFieldDefaultValueChange,
        getComputedFieldEditing,
        removeBlockedReasons,
    } = useEditEntityTemplate();

    const [activeStepOrder, setActiveStepOrder] = useState(1);

    const activeStep = templateFromBackend?.steps.find((s) => s.order === activeStepOrder)!;

    // Create wrapper functions that include the stepId
    const handleFieldsReorder = async (movedFieldId: string, newPosition: number) => {
        await handleFieldReorder(activeStep.id, movedFieldId, newPosition);
    };

    const handleFieldDeleteWrapper = async (fieldId: string) => {
        await handleFieldDelete(activeStep.id, fieldId);
    };

    const handleFieldRequiredToggleWrapper = async (fieldId: string, required: boolean) => {
        await handleFieldRequiredToggle(activeStep.id, fieldId, required);
    };

    const handleFieldRequiredAtCreationToggleWrapper = async (fieldId: string, requiredAtCreation: boolean) => {
        await handleFieldRequiredAtCreationToggle(activeStep.id, fieldId, requiredAtCreation);
    };

    const handleFieldAddWrapper = async (field: EntityTemplateField) => {
        await handleFieldAdd(activeStep.id, field);
    };

    const handleFieldDefaultValueChangeWrapper = handleFieldDefaultValueChange
        ? async (fieldId: string, defaultValue: unknown) => await handleFieldDefaultValueChange(activeStep.id, fieldId, defaultValue)
        : undefined;

    return (
        <React.Fragment>
            <DotLegalPageHeader breadCrumbs={breadCrumbs} />
            {isLoading ? (
                <React.Fragment>
                    <DotLegalFullScreenSpinner />
                </React.Fragment>
            ) : (
                <DotLegalStepper
                    graphicType={{ wave: "purpleWave" }}
                    steps={templateFromBackend!.steps.map((s) => {
                        return {
                            name: s.name,
                            stepType: s.order,
                            disabled: false,
                            error: false,
                            stepHover: undefined,
                            helpTextContent: undefined,
                            stepValidated: undefined,
                            visited: false,
                            helpTextHeader: undefined,
                        };
                    })}
                    activeStep={activeStepOrder}
                    header={activeStep.name}
                    inProgess={isLoading}
                    onStepClick={(step) => setActiveStepOrder(step)}
                    onSaveClick={async () => {
                        navigate(urlProvider.getEditEntityTemplateSteps(id!));
                    }}
                    buttonWidth={160}
                    overviewButtonText={translateString("manageSteps")}
                >
                    <TemplateStepEditor
                        step={activeStep}
                        isStandardTemplate={templateFromBackend?.isStandardTemplate ?? false}
                        availableStandardFields={availableStandardFields}
                        availableCustomFields={customInputFields ?? []}
                        onFieldsReorder={handleFieldsReorder}
                        onFieldDelete={handleFieldDeleteWrapper}
                        onFieldRequiredToggle={handleFieldRequiredToggleWrapper}
                        onFieldRequiredAtCreationToggle={handleFieldRequiredAtCreationToggleWrapper}
                        onFieldAdd={handleFieldAddWrapper}
                        onFieldDefaultValueChange={handleFieldDefaultValueChangeWrapper}
                        templateEntityType={templateFromBackend?.templateType}
                        computedFieldEditing={getComputedFieldEditing(activeStep.id)}
                        removeBlockedReasons={removeBlockedReasons}
                    />
                </DotLegalStepper>
            )}
        </React.Fragment>
    );
}

export default EditEntityTemplate;
